import type { ComponentInit } from "@scripts/registry";
import { headerOffset, scrollToElement } from "@scripts/scrollController";

import { initAccount } from "./SiteHeader.account";
import { initBrand } from "./SiteHeader.brand";
import { initDrawer } from "./SiteHeader.drawer";
import { initMega } from "./SiteHeader.mega";
import { EBar, dropHeaderStore, getHeaderStore } from "./SiteHeader.store";

const SKIP = "[data-fa-header-skip]";

const COLLAPSE_AFTER = 300;
const TRAVEL_THRESHOLD = 60;

export const initSiteHeader: ComponentInit = (root) => {
	const listeners = new AbortController();
	let frame: number | undefined;
	const adopted = new Set<Element>();
	const store = getHeaderStore(root);
	let compact = false;
	let lastY = window.scrollY;
	let travel = 0;

	const apply = () => {
		frame = undefined;

		const y = window.scrollY;
		const delta = y - lastY;

		lastY = y;

		/* Reset on a direction change, so jitter never accumulates towards either threshold and
		   the transition cannot be restarted part-way through. */
		if (delta !== 0 && Math.sign(delta) !== Math.sign(travel)) travel = 0;

		travel += delta;

		if (travel > TRAVEL_THRESHOLD && y > COLLAPSE_AFTER) compact = true;
		if (travel < -TRAVEL_THRESHOLD || y < 1) compact = false;

		store.setBar(y < 1 ? EBar.INITIAL : compact ? EBar.COLLAPSED : EBar.SCROLLED);
	};

	/* Coalesced, so a fast scroll reads the position once per frame rather than per event. */
	const onScroll = () => {
		frame ??= window.requestAnimationFrame(apply);
	};

	window.addEventListener("scroll", onScroll, {
		passive: true,
		signal: listeners.signal,
	});

	const skip = root.querySelector<HTMLAnchorElement>(SKIP);

	/* A panel holding the page inerts `<main>`, and a live skip link pointing at an inert element
	   lands focus on `<body>` instead. Asked of the hold and not of the field, because the account
	   dropdown takes the field without inerting anything. */
	const unfollowPage = store.subscribe(() => {
		if (skip) skip.inert = store.pageHeld();
	});

	skip?.addEventListener(
		"click",
		(event) => {
			const id = skip.getAttribute("href")?.slice(1) ?? "";
			const target = id === "" ? null : document.getElementById(id);

			if (!target) return;

			/* stopPropagation, not just preventDefault: Lenis binds its anchor handler on the
			   window and never checks defaultPrevented, so its no-offset scroll would land last. */
			event.preventDefault();
			event.stopPropagation();

			/* The target is the vendor's markup, so the attribute is borrowed and given back. */
			if (!target.hasAttribute("tabindex")) {
				target.setAttribute("tabindex", "-1");
				adopted.add(target);
			}

			target.focus({ preventScroll: true });

			scrollToElement(target, headerOffset(root));
		},
		{ signal: listeners.signal },
	);

	const drawer = initDrawer(root, listeners.signal);
	const mega = initMega(root, listeners.signal);
	const brand = initBrand(root, listeners.signal);
	const account = initAccount(root, listeners.signal);

	apply();

	return () => {
		unfollowPage();
		if (skip) skip.inert = false;

		listeners.abort();
		if (frame !== undefined) window.cancelAnimationFrame(frame);

		drawer();
		mega();
		brand();
		account();

		for (const target of adopted) target.removeAttribute("tabindex");
		dropHeaderStore(root);
	};
};
